import { IError } from './BusinessInfo';
import {
    validateBirth,
    validateBusinessName,
    validateMI,
    validateOwnership,
    validatePhone,
    validateZipCanada,
    validateZipUSA,
} from '../utils';

interface IValuesApplicant {
    status: string;
    name: string;
    type: string;
    incorporation: string;
    federal: string;
    province: string;
    years: string | number;
    months: string | number;
}
interface IErrorsApplicant {
    status: IError;
    name: IError;
    type: IError;
    incorporation: IError;
    federal: IError;
    province: IError;
    years: IError;
    months: IError;
}

interface IValuesBusiness {
    addressLine1: string;
    addressLine2: string;
    city: string;
    province: string;
    postalCode: string;
    telephone: string;
}

interface IErrorsBusiness {
    addressLine1: IError;
    addressLine2: IError;
    city: IError;
    province: IError;
    postalCode: IError;
    telephone: IError;
}

interface IValuesOwner {
    firstName: string;
    mi: string;
    lastName: string;
    title: string;
    ownership: string;
}

interface IErrorsOwner {
    firstName: IError;
    mi: IError;
    lastName: IError;
    title: IError;
    ownership: IError;
}

interface IValuesResponsible {
    firstName: string;
    mi: string;
    lastName: string;
    telephone: string;
}

interface IErrorsResponsible {
    firstName: IError;
    mi: IError;
    lastName: IError;
    telephone: IError;
}

interface IValuesDriver {
    relationship: string;
    firstName: string;
    mi: string;
    lastName: string;
    telephone: string;
    birthDate: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    province: string;
    postalCode: string;
    years: string;
    months: string;
    license: string;
    expirationDate: string;
}

interface IErrorsDriver {
    relationship: IError;
    firstName: IError;
    mi: IError;
    lastName: IError;
    telephone: IError;
    birthDate: IError;
    addressLine1: IError;
    addressLine2: IError;
    city: IError;
    province: IError;
    postalCode: IError;
    years: IError;
    months: IError;
    license: IError;
    expirationDate: IError;
}

interface IValuesReferences {
    previous: Array<{ value: string }>;
    bank: string;
    checking: boolean;
    savings: boolean;
    creditLine: boolean;
    branch: string;
    accountNumber: string;
    addressLine1: string;
    addressLine2: string;
    city: string;
    province: string;
    postalCode: string;
    telephone: string;
}

interface IErrorsReferences {
    previous: Array<IError>;
    bank: IError;
    checking: IError;
    savings: IError;
    creditLine: IError;
    branch: IError;
    accountNumber: IError;
    addressLine1: IError;
    addressLine2: IError;
    city: IError;
    province: IError;
    postalCode: IError;
    telephone: IError;
}

function validateStatus(status: string) {
    return !status.length;
}

function validateName(name: string, translation: any): [boolean, string] {
    let error = false;
    let msg = '';
    if (!name.length) {
        error = true;
        msg = translation.REQUIRED_FIELD;
    } else if (name.length > Number('50') || !validateBusinessName(name)) {
        error = true;
        msg = translation.INVALID_FIELD;
    }
    return [error, msg];
}

export const setValidationApplicant = (
    values: IValuesApplicant,
    errors: IErrorsApplicant,
    saveErrors: Function,
    translation: any,
) => {
    const aux = { ...errors };
    aux.status.error = validateStatus(values.status);
    aux.status.msg = aux.status.error ? translation.REQUIRED_FIELD : '';

    [aux.name.error, aux.name.msg] = validateName(values.name, translation);

    if (
        values.incorporation.length &&
        (+values.incorporation < Number('1927') ||
            +values.incorporation > new Date().getFullYear())
    ) {
        aux.incorporation.error = true;
        aux.incorporation.msg = translation.INVALID_FIELD;
    } else {
        aux.incorporation.error = false;
        aux.incorporation.msg = '';
    }

    if (
        values.federal.length &&
        values.federal === 'province' &&
        values.province === ''
    ) {
        aux.province.error = true;
        aux.province.msg = translation.REQUIRED_FIELD;
    } else {
        aux.province.error = false;
        aux.province.msg = '';
    }

    const twoDigitsRegex = /^\d{1,2}$/;
    if (
        (twoDigitsRegex.test(values.years.toString()) &&
            +values.years > Number('100') &&
            +values.years < Number('0')) ||
        +values.years === Number('0')
    ) {
        aux.years.error = true;
        aux.years.msg = translation.INVALID_FIELD;
    } else {
        aux.years.error = false;
        aux.years.msg = '';
    }

    if (
        twoDigitsRegex.test(values.months.toString()) &&
        +values.months > Number('12') &&
        +values.months < Number('0')
    ) {
        aux.months.error = true;
        aux.months.msg = translation.INVALID_FIELD;
    } else {
        aux.months.error = false;
        aux.months.msg = '';
    }

    saveErrors('application', aux);
};

function validateAddressLine1(
    addressLine1: string,
    translation: any,
    isRequired: boolean,
): [boolean, string] {
    let error = false;
    let msg = '';

    if (isRequired && !addressLine1.length) {
        error = true;
        msg = translation.businessComercial.REQUIRED_FIELD;
    } else if (addressLine1.length > Number('40')) {
        error = true;
        msg = translation.businessComercial.INVALID_FIELD;
    }

    return [error, msg];
}

function validateAddressLine2(
    addressLine2: string,
    translation: any,
): [boolean, string] {
    let error = false;
    let msg = '';

    if (addressLine2.length > Number('25')) {
        error = true;
        msg = translation.businessComercial.INVALID_FIELD;
    }

    return [error, msg];
}

function validateCity(
    city: string,
    translation: any,
    isRequired: boolean,
): [boolean, string] {
    let error = false;
    let msg = '';

    if (isRequired && !city.length) {
        error = true;
        msg = translation.businessComercial.REQUIRED_FIELD;
    } else if (city.length > Number('30')) {
        error = true;
        msg = translation.businessComercial.INVALID_FIELD;
    }

    return [error, msg];
}

function validateProvince(
    province: string,
    translation: any,
): [boolean, string] {
    let error = false;
    let msg = '';

    if (!province.length) {
        error = true;
        msg = translation.businessComercial.REQUIRED_FIELD;
    }

    return [error, msg];
}

export const setValidationBusiness = (
    values: IValuesBusiness,
    errors: IErrorsBusiness,
    saveErrors: Function,
    translation: any,
    isCanada: boolean,
) => {
    const aux = { ...errors };

    [aux.addressLine1.error, aux.addressLine1.msg] = validateAddressLine1(
        values.addressLine1,
        translation,
        true,
    );

    [aux.addressLine2.error, aux.addressLine2.msg] = validateAddressLine2(
        values.addressLine2,
        translation,
    );

    [aux.city.error, aux.city.msg] = validateCity(
        values.city,
        translation,
        true,
    );

    [aux.province.error, aux.province.msg] = validateProvince(
        values.province,
        translation,
    );

    [aux.postalCode.error, aux.postalCode.msg] = validatePostalCode(
        values.postalCode,
        translation,
        isCanada,
        true,
    );

    if (values.telephone.length === Number('0')) {
        aux.telephone.error = true;
        aux.telephone.msg = translation.businessComercial.REQUIRED_FIELD;
    } else if (values.telephone.length && !validatePhone(values.telephone)) {
        aux.telephone.error = true;
        aux.telephone.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.telephone.error = false;
        aux.telephone.msg = '';
    }
    saveErrors('business', aux);
};

export const setValidationOwner = (
    values: IValuesOwner,
    errors: IErrorsOwner,
    saveErrors: Function,
    translation: any,
) => {
    const aux = { ...errors };

    [
        aux.firstName.error,
        aux.firstName.msg,
        aux.mi.error,
        aux.mi.msg,
        aux.lastName.error,
        aux.lastName.msg,
    ] = validateFullName(
        values.firstName,
        values.mi,
        values.lastName,
        translation,
    );

    if (values.ownership.length && !validateOwnership(values.ownership)) {
        aux.ownership.error = true;
        aux.ownership.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.ownership.error = false;
        aux.ownership.msg = '';
    }
    saveErrors('owner', aux);
};

export const setValidationResponsible = (
    values: IValuesResponsible,
    errors: IErrorsResponsible,
    saveErrors: Function,
    translation: any,
) => {
    const aux = { ...errors };

    [
        aux.firstName.error,
        aux.firstName.msg,
        aux.mi.error,
        aux.mi.msg,
        aux.lastName.error,
        aux.lastName.msg,
    ] = validateFullName(
        values.firstName,
        values.mi,
        values.lastName,
        translation,
    );

    if (values.telephone.length && !validatePhone(values.telephone)) {
        aux.telephone.error = true;
        aux.telephone.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.telephone.error = false;
        aux.telephone.msg = '';
    }
    saveErrors('responsible', aux);
};

function validateFullName(
    firstName: string,
    middleInitial: string,
    lastName: string,
    translation: any,
): [boolean, string, boolean, string, boolean, string] {
    let firstNameError = false;
    let firstNameMsg = '';
    if (firstName.length && firstName.length > Number('30')) {
        firstNameError = true;
        firstNameMsg = translation.businessComercial.INVALID_FIELD;
    }

    let middleInitialError = false;
    let middleInitialMsg = '';
    if (middleInitial.length && !validateMI(middleInitial)) {
        middleInitialError = true;
        middleInitialMsg = translation.businessComercial.INVALID_FIELD;
    }

    let lastNameError = false;
    let lastNameMsg = '';
    if (lastName.length && lastName.length > Number('30')) {
        lastNameError = true;
        lastNameMsg = translation.businessComercial.INVALID_FIELD;
    }

    return [
        firstNameError,
        firstNameMsg,
        middleInitialError,
        middleInitialMsg,
        lastNameError,
        lastNameMsg,
    ];
}

function validatePostalCode(
    postalCode: string,
    translation: any,
    isCanada: boolean,
    required = false,
): [boolean, string] {
    let postalCodeError = false;
    let postalCodeMsg = '';
    if (postalCode.length) {
        let validation;
        if (isCanada) {
            validation = validateZipCanada(postalCode);
        } else {
            validation = validateZipUSA(postalCode);
        }

        if (validation && isCanada) {
            postalCodeError = true;
            postalCodeMsg =
                translation.occupationIndividualInformation.POSTAL_ERR;
        } else if (validation) {
            postalCodeError = true;
            postalCodeMsg = translation.occupationIndividualInformation.ZIP_ERR;
        }
    } else {
        if (required) {
            postalCodeError = true;
            if (isCanada)
                postalCodeMsg =
                    translation.occupationIndividualInformation.POSTAL_ERR;
            else
                postalCodeMsg =
                    translation.occupationIndividualInformation.ZIP_ERR;
        }
    }

    return [postalCodeError, postalCodeMsg];
}

function validateYearOrMonth(
    value: string,
    isYear: boolean,
    translation: any,
): [boolean, string] {
    let error = false;
    let msg = '';
    const maxLimit = isYear ? Number('100') : Number('12');

    if (
        value.length &&
        value.length < Number('3') &&
        +value > maxLimit &&
        +value < Number('0')
    ) {
        error = true;
        msg = translation.INVALID_FIELD;
    }
    return [error, msg];
}

export const setValidationDriver = (
    values: IValuesDriver,
    errors: IErrorsDriver,
    saveErrors: Function,
    translation: any,
    isCanada: boolean,
) => {
    const aux = { ...errors };

    [
        aux.firstName.error,
        aux.firstName.msg,
        aux.mi.error,
        aux.mi.msg,
        aux.lastName.error,
        aux.lastName.msg,
    ] = validateFullName(
        values.firstName,
        values.mi,
        values.lastName,
        translation,
    );

    if (values.telephone.length && !validatePhone(values.telephone)) {
        aux.telephone.error = true;
        aux.telephone.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.telephone.error = false;
        aux.telephone.msg = '';
    }

    if (values.birthDate.length && !validateBirth(values.birthDate)) {
        aux.birthDate.error = true;
        aux.birthDate.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.birthDate.error = false;
        aux.birthDate.msg = '';
    }

    if (
        values.addressLine1.length &&
        values.addressLine1.length <= Number('40')
    ) {
        aux.addressLine1.error = false;
        aux.addressLine1.msg = '';
    }

    [aux.addressLine2.error, aux.addressLine2.msg] = validateAddressLine2(
        values.addressLine2,
        translation,
    );

    if (values.city.length && values.city.length > Number('30')) {
        aux.city.error = false;
        aux.city.msg = '';
    }

    [aux.postalCode.error, aux.postalCode.msg] = validatePostalCode(
        values.postalCode,
        translation,
        isCanada,
    );

    [aux.years.error, aux.years.msg] = validateYearOrMonth(
        values.years,
        true,
        translation,
    );
    [aux.months.error, aux.months.msg] = validateYearOrMonth(
        values.months,
        false,
        translation,
    );

    if (values.expirationDate.length && !validateBirth(values.expirationDate)) {
        aux.expirationDate.error = true;
        aux.expirationDate.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.expirationDate.error = false;
        aux.expirationDate.msg = '';
    }
    saveErrors('driver', aux);
};

export const setValidationReferences = (
    values: IValuesReferences,
    errors: IErrorsReferences,
    saveErrors: Function,
    translation: any,
    isCanada: boolean,
) => {
    const aux = { ...errors };

    if (values.bank.length && values.bank.length > Number('30')) {
        aux.bank.error = true;
        aux.bank.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.bank.error = false;
        aux.bank.msg = '';
    }

    if (values.telephone.length && !validatePhone(values.telephone)) {
        aux.telephone.error = true;
        aux.telephone.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.telephone.error = false;
        aux.telephone.msg = '';
    }

    if (values.branch.length && values.branch.length > Number('5')) {
        aux.branch.error = true;
        aux.branch.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.branch.error = false;
        aux.branch.msg = '';
    }

    if (
        values.accountNumber.length &&
        values.accountNumber.length > Number('12')
    ) {
        aux.accountNumber.error = true;
        aux.accountNumber.msg = translation.businessComercial.INVALID_FIELD;
    } else {
        aux.accountNumber.error = false;
        aux.accountNumber.msg = '';
    }

    [aux.addressLine1.error, aux.addressLine1.msg] = validateAddressLine1(
        values.addressLine1,
        translation,
        false,
    );

    [aux.addressLine2.error, aux.addressLine2.msg] = validateAddressLine2(
        values.addressLine2,
        translation,
    );

    [aux.city.error, aux.city.msg] = validateCity(
        values.city,
        translation,
        false,
    );

    [aux.postalCode.error, aux.postalCode.msg] = validatePostalCode(
        values.postalCode,
        translation,
        isCanada,
    );

    saveErrors('references', aux);
};
